import { useEffect, useState } from 'react';
import { read_cookie } from 'sfcookies';
import { hostUrl } from '../../../utils/url-manager';
import { AclResourceNode } from '../../../../types/entities';

export type AclTree = {
    /** The root of the resource tree, or null until it has loaded. */
    tree: AclResourceNode | null,
    /** The resources the signed-in admin is entitled to grant to somebody else. */
    grantable: string[],
    loading: boolean,
    /** The reason it could not be loaded, shown rather than swallowed. */
    error: string | null,
}

/**
 * Load the ACL resource tree from HQ-API.
 *
 * `GET /hq/acl/tree` is derived entirely from `config/acl.php`, so this is the code half of the
 * ACL arriving in the browser. It cannot be a data provider call: the tree is not a resource, it
 * has no ids react-admin would recognise, and it answers the caller's own grants alongside it.
 *
 * `grantable` is what the checkbox tree greys out. Nobody may grant a resource they cannot use
 * themselves, and HQ-API refuses it either way; disabling the box is so an admin can see why
 * rather than discovering it on save.
 */
export const useAclTree: () => AclTree = (): AclTree => {
    const [tree, setTree] = useState<AclResourceNode | null>(null);
    const [grantable, setGrantable] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect((): (() => void) => {
        let abandoned: boolean = false;

        const load = async (): Promise<void> => {
            try {
                const response: Response = await fetch(new Request(hostUrl + 'hq/acl/tree', {
                    method: 'GET',
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'token': read_cookie('auth'),
                        'hq': 'hq',
                    }),
                }));

                const body: any = await response.json().catch((): null => null);
                if (abandoned) {
                    return;
                }

                if (!response.ok) {
                    setError(typeof body?.error === 'string' ? body.error : 'The resource tree could not be loaded');
                } else {
                    setTree(body?.tree ?? null);
                    setGrantable(Array.isArray(body?.grantable) ? body.grantable : []);
                }
            } catch (caught) {
                console.log(caught);
                if (!abandoned) {
                    setError('The resource tree could not be loaded');
                }
            } finally {
                if (!abandoned) {
                    setLoading(false);
                }
            }
        };

        void load();

        // The role screen is reachable and leavable quickly, and setting state after unmount is a
        // React warning that reads as a bug in whatever rendered next.
        return (): void => {
            abandoned = true;
        };
    }, []);

    return { tree, grantable, loading, error };
}
